import * as React from "react";

import { ClickOutsideArea } from "../constants";
import { ICreateNewOption, IOption } from "../types";

import { ICustomOption, IOptionMixin } from "./types";
import { getLabel, getNextIndex, getValue } from "./utils";

const CLOSE_KEYS = ["Tab", "Escape"];
const RELEVANT_KEYS = ["ArrowDown", "ArrowUp", "Enter"];

interface UseDropdownInteractionsProps<T extends string> {
  createNewOption?: ICreateNewOption;
  customOption?: ICustomOption<T>;
  filteredOptions: IOptionMixin<T>[];
  highlightIndex: number;
  isCustomizingOption: boolean;
  onDropdownClose?: () => void;
  onDropdownOpen?: () => void;
  onSelect: (option: IOption<T>) => void;
  open: boolean;
  options: IOption<T>[];
  refs: React.MutableRefObject<Record<number, HTMLButtonElement | null>>;
  searchValue?: string;
  setHighlightIndex: React.Dispatch<React.SetStateAction<number>>;
  setIsCustomizingOption: React.Dispatch<React.SetStateAction<boolean>>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSearchValue: React.Dispatch<React.SetStateAction<string | undefined>>;
  targetRef: React.MutableRefObject<HTMLInputElement | null>;
  value?: T;
}

export const useDropdownInteractions = <T extends string>({
  createNewOption,
  customOption,
  filteredOptions,
  highlightIndex,
  isCustomizingOption,
  onDropdownClose,
  onDropdownOpen,
  onSelect,
  open,
  options,
  refs,
  searchValue,
  setHighlightIndex,
  setIsCustomizingOption,
  setOpen,
  setSearchValue,
  targetRef,
  value,
}: UseDropdownInteractionsProps<T>) => {
  const enterCustomValue = React.useCallback(() => {
    if (customOption && searchValue) {
      customOption.onValueChange(searchValue);
      setIsCustomizingOption(false);
      setSearchValue(undefined);
    }
  }, [customOption, searchValue, setIsCustomizingOption, setSearchValue]);

  const handleValueSelect = React.useCallback(
    (optionValue: T) => {
      const option =
        customOption && getValue(customOption) === optionValue
          ? customOption
          : options.find((e) => getValue(e) === optionValue);
      onSelect(option!);
      setOpen(false);
      setSearchValue(undefined);
    },
    [customOption, onSelect, options, setOpen, setSearchValue]
  );

  const handleOptionSelect = React.useCallback(
    (selectIndex: number) => {
      const selectedOption = filteredOptions[selectIndex];
      const value = selectedOption ? getValue(selectedOption) : undefined;

      if (value !== undefined) {
        handleValueSelect(value);
      }
    },
    [filteredOptions, handleValueSelect]
  );

  const handleClose = React.useCallback(() => {
    onDropdownClose?.();
    setSearchValue(undefined);
    setOpen(false);
  }, [onDropdownClose, setOpen, setSearchValue]);

  const handleInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === "Enter") {
        if (isCustomizingOption) {
          enterCustomValue();
        } else if (createNewOption && filteredOptions.length === 0) {
          setSearchValue(undefined);
          if (createNewOption.closeDropdown) {
            setOpen(false);
          }
          createNewOption?.onClick?.(searchValue);
        }
      }
    },
    [
      createNewOption,
      enterCustomValue,
      filteredOptions.length,
      isCustomizingOption,
      searchValue,
      setOpen,
      setSearchValue,
    ]
  );

  /**
   * Handles click outside the dropdown.
   * @param clickOutsideArea - The area that was clicked outside of (i.e. the input or the dropdown menu).
   */
  const handleClickOutside = React.useCallback(
    (clickOutsideArea?: ClickOutsideArea) => {
      if (isCustomizingOption) {
        enterCustomValue();
      } else if (clickOutsideArea !== ClickOutsideArea.INPUT) {
        // Do not close if click was only outside the input (i.e. still inside the dropdown menu)
        handleClose();
      }
    },
    [isCustomizingOption, enterCustomValue, handleClose]
  );

  const handleOpen = React.useCallback(() => {
    onDropdownOpen?.();
    setOpen(true);
    if (!value) {
      return;
    }

    const selectedIndex =
      customOption && customOption.value === value
        ? options.length
        : options.findIndex((o) => o.value === value);

    if (selectedIndex !== -1) {
      setHighlightIndex(selectedIndex);
    }
  }, [
    customOption,
    onDropdownOpen,
    options,
    setHighlightIndex,
    setOpen,
    value,
  ]);

  const scrollIntoView = React.useCallback(
    (index: number) => {
      const ref = refs.current[index];
      if (!ref) {
        return;
      }
      ref.scrollIntoView?.({
        block: "nearest",
      });
    },
    [refs]
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (CLOSE_KEYS.includes(event.key)) {
        handleClose();
        return;
      }

      if (!RELEVANT_KEYS.includes(event.key)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (open) {
        let nextIndex = highlightIndex;
        switch (event.key) {
          case "ArrowDown":
          case "ArrowUp":
            nextIndex = getNextIndex(
              event.key,
              filteredOptions,
              highlightIndex
            );
            break;
          case "Enter":
            handleOptionSelect(highlightIndex);
            break;
          default:
            break;
        }

        scrollIntoView(nextIndex);
        setHighlightIndex(nextIndex);
      } else {
        handleOpen();
      }
    },
    [
      filteredOptions,
      handleClose,
      handleOpen,
      handleOptionSelect,
      highlightIndex,
      open,
      scrollIntoView,
      setHighlightIndex,
    ]
  );

  const handleInputSearch = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!isCustomizingOption) {
        handleOpen();

        const search = event.target.value;
        setSearchValue(search);

        if (search === "") {
          setHighlightIndex(0);
          scrollIntoView(0);
          return;
        }

        const lowerCaseSearch = search.toLowerCase();
        const optionIndex = options.findIndex((e) =>
          getLabel(e).toLowerCase().startsWith(lowerCaseSearch)
        );
        if (optionIndex !== -1) {
          setHighlightIndex(optionIndex);
          scrollIntoView(optionIndex);
        }
      } else {
        setSearchValue(event.target.value);
      }
    },
    [
      isCustomizingOption,
      handleOpen,
      setSearchValue,
      options,
      setHighlightIndex,
      scrollIntoView,
    ]
  );

  const handleCustomOptionEdit = React.useCallback(() => {
    setIsCustomizingOption(true);
    handleClose();
    targetRef.current?.focus();
    setSearchValue(customOption?.hasEdited ? customOption.value : "");
  }, [
    customOption,
    handleClose,
    setIsCustomizingOption,
    setSearchValue,
    targetRef,
  ]);

  const resetSearchValue = React.useCallback(() => {
    setSearchValue(undefined);
  }, [setSearchValue]);

  const closeDropdown = React.useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleInputClick = React.useCallback(() => {
    targetRef.current?.setSelectionRange(0, targetRef.current.value.length);
    if (open) {
      handleClose();
    } else {
      handleOpen();
    }
  }, [handleClose, handleOpen, open, targetRef]);

  return {
    closeDropdown,
    handleClickOutside,
    handleInputClick,
    handleInputKeyDown,
    handleKeyDown,
    handleValueSelect,
    handleOptionSelect,
    handleInputSearch,
    handleCustomOptionEdit,
    resetSearchValue,
    scrollIntoView,
  };
};
